import React from 'react';
import { X } from 'lucide-react';
import type { AuditProgressStep, AuditStepMeta } from '../api/seoApi';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './AuditProgressPanel.module.css';

const REASON_LABELS: Record<string, string> = {
  not_connected: 'Not connected',
  cached: 'Using cached data',
};

const TRANSPORT_LABELS: Record<string, string> = {
  http: 'direct HTTP',
  playwright: 'browser rendering',
  firecrawl: 'Firecrawl',
};

function findStep(steps: AuditProgressStep[], id: string): AuditProgressStep | undefined {
  return steps.find((s) => s.id === id);
}

/** Combine robots.txt/sitemap/llms.txt into one row — these are individually
 * uninteresting and non-critical, so the group just reflects whether it's
 * done as a whole rather than surfacing per-item detail. */
function combineStatus(subs: Array<AuditProgressStep | undefined>): AuditProgressStep['status'] {
  const present = subs.filter((s): s is AuditProgressStep => !!s);
  if (present.length === 0) return 'pending';
  if (present.some((s) => s.status === 'running')) return 'running';
  if (present.every((s) => s.status !== 'pending')) return 'done';
  // Some subs finished but others haven't started yet — e.g. the Playwright
  // transport defers llms.txt until after the page crawl. That's genuine
  // in-progress work, not "not started", so show the spinner rather than
  // the empty pending dot.
  if (present.some((s) => s.status !== 'pending')) return 'running';
  return 'pending';
}

function crawlLabel(meta: AuditStepMeta | undefined, isDone: boolean): string | null {
  if (meta?.pagesCrawled == null) return null;
  const transport = meta.transport ? (TRANSPORT_LABELS[meta.transport] ?? meta.transport) : null;
  const suffix = isDone && transport ? ` via ${transport}` : '';
  return `${meta.pagesCrawled} pages crawled${suffix}`;
}

function gscLabel(meta: AuditStepMeta | undefined): string | null {
  if (!meta) return null;
  if (meta.reason) return REASON_LABELS[meta.reason] ?? null;
  return meta.keywordCount != null ? `${meta.keywordCount} keywords synced` : null;
}

function gbpLabel(meta: AuditStepMeta | undefined): string | null {
  if (!meta) return null;
  if (meta.reason) return REASON_LABELS[meta.reason] ?? null;
  return meta.reviewCount != null ? `${meta.reviewCount} reviews synced` : null;
}

function sslLabel(meta: AuditStepMeta | undefined): string | null {
  if (!meta?.valid) return null;
  return meta.daysUntilExpiry != null ? `Valid · ${meta.daysUntilExpiry}d remaining` : 'Valid';
}

function pagespeedLabel(meta: AuditStepMeta | undefined): string | null {
  if (meta?.mobileScore == null) return null;
  return meta.cached
    ? `Mobile score: ${meta.mobileScore} · cached`
    : `Mobile score: ${meta.mobileScore}`;
}

function aiAuditLabel(meta: AuditStepMeta | undefined): string | null {
  return meta?.overallScore != null ? `Score: ${meta.overallScore} (${meta.grade})` : null;
}

interface DisplayStep {
  id: string;
  label: string;
  status: AuditProgressStep['status'];
  metaLabel: string | null;
  error?: string;
}

/**
 * Collapses the raw per-backend-step progress array into a fixed 7-row
 * template: robots.txt/sitemap/llms.txt combine into one row, crawl-transport
 * escalation attempts (http/playwright/firecrawl) collapse into a single
 * "Crawling pages" row that names whichever transport ultimately succeeded,
 * and every row is always shown (defaulting to 'pending') rather than only
 * appearing once the backend has actually reached that stage.
 */
function buildDisplaySteps(steps: AuditProgressStep[]): DisplayStep[] {
  const siteDataSteps = ['robots_txt', 'sitemap', 'llms_txt'].map((id) => findStep(steps, id));
  const crawl = findStep(steps, 'crawl_pages');
  const gsc = findStep(steps, 'gsc_sync');
  const gbp = findStep(steps, 'gbp_sync');
  const ssl = findStep(steps, 'ssl_check');
  const pagespeed = findStep(steps, 'pagespeed');
  const aiAudit = findStep(steps, 'ai_audit');

  return [
    {
      id: 'site_data',
      label: 'Fetching robots.txt, sitemap & llms.txt',
      status: combineStatus(siteDataSteps),
      metaLabel: null,
    },
    {
      id: 'crawl_pages',
      label: 'Crawling pages',
      status: crawl?.status ?? 'pending',
      metaLabel: crawlLabel(crawl?.meta, crawl?.status === 'done'),
      error: crawl?.error,
    },
    {
      id: 'gsc_sync',
      label: 'Syncing Google Search Console',
      status: gsc?.status ?? 'pending',
      metaLabel: gscLabel(gsc?.meta),
      error: gsc?.error,
    },
    {
      id: 'gbp_sync',
      label: 'Syncing Google Business Profile',
      status: gbp?.status ?? 'pending',
      metaLabel: gbpLabel(gbp?.meta),
      error: gbp?.error,
    },
    {
      id: 'ssl_check',
      label: 'SSL certificate check',
      status: ssl?.status ?? 'pending',
      metaLabel: sslLabel(ssl?.meta),
      error: ssl?.error,
    },
    {
      id: 'pagespeed',
      label: 'PageSpeed analysis',
      status: pagespeed?.status ?? 'pending',
      metaLabel: pagespeedLabel(pagespeed?.meta),
      error: pagespeed?.error,
    },
    {
      id: 'ai_audit',
      label: 'Running AI audit',
      status: aiAudit?.status ?? 'pending',
      metaLabel: aiAuditLabel(aiAudit?.meta),
      error: aiAudit?.error,
    },
  ];
}

function StepIcon({ status }: { status: AuditProgressStep['status'] }) {
  if (status === 'done') return <span className={patterns.stepIconDone}>&#10003;</span>;
  if (status === 'failed') return <span className={patterns.stepIconFailed}>&#10007;</span>;
  if (status === 'skipped') return <span className={patterns.stepIconSkipped}>&#8211;</span>;
  if (status === 'running') return <span className={patterns.stepIconRunning} />;
  return <span className={patterns.stepIconPending} />;
}

interface Props {
  steps: AuditProgressStep[];
  status?: string;
  errorMessage?: string | null;
  onDismiss?: () => void;
  onCancel?: () => void;
  isCancelling?: boolean;
}

export function AuditProgressPanel({
  steps,
  status,
  errorMessage,
  onDismiss,
  onCancel,
  isCancelling,
}: Props) {
  const displaySteps = buildDisplaySteps(steps);
  const doneCount = displaySteps.filter(
    (s) => s.status === 'done' || s.status === 'failed' || s.status === 'skipped',
  ).length;
  const pct = Math.round((doneCount / displaySteps.length) * 100);
  const isDone = status === 'completed' || status === 'failed';

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.title}>
          {isDone
            ? status === 'completed'
              ? 'Audit complete'
              : 'Audit failed'
            : 'Audit in progress'}
        </div>
        <div className={styles.headerRight}>
          {!isDone && <div className={styles.pct}>{pct}%</div>}
          {!isDone && onCancel && (
            <button className={styles.cancelBtn} onClick={onCancel} disabled={isCancelling}>
              {isCancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
          {isDone && onDismiss && (
            <button className={styles.dismissBtn} onClick={onDismiss} aria-label="Dismiss">
              <X size={14} aria-hidden />
            </button>
          )}
        </div>
      </div>

      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>

      <div className={styles.steps}>
        {displaySteps.map((step) => (
          <div key={step.id} className={styles.step}>
            <StepIcon status={step.status} />
            <span className={patterns.stepListLabel}>
              {step.label}
              {step.status !== 'failed' && step.metaLabel && (
                <span className={patterns.stepListMeta}> &middot; {step.metaLabel}</span>
              )}
            </span>
            {step.status === 'failed' && step.error && (
              <span className={patterns.stepListError}>{step.error}</span>
            )}
          </div>
        ))}
      </div>

      {errorMessage && !displaySteps.some((s) => s.status === 'failed' && s.error) && (
        <div className={styles.errorBanner}>{errorMessage}</div>
      )}
    </div>
  );
}
